import i18nFactory from '@yandex-int/i18n';
import { FetchResult } from 'apollo-boost';
import {
    compact,
    forEach,
    get,
    isArray,
    isEqual,
    mergeWith,
    some,
    values
} from 'lodash';
import { Action } from 'redux';
import { Epic, ofType } from 'redux-observable';
import { EMPTY, forkJoin, from, merge, of, timer } from 'rxjs';
import {
    catchError,
    exhaustMap,
    filter,
    map,
    mergeMap,
    switchMap,
    takeUntil
} from 'rxjs/operators';
import { Observable } from 'rxjs/Rx';

import {
    changeLocation,
    changeLocationHard,
    dummyAction,
    updateCryptaSegments,
    updateIndoorMapData,
    updateOutdoorMapData,
    updateRegions,
    updateStringDicts
} from '../../actions/common';
import { updateDatesIntervalEditors } from '../../actions/datesIntervalEditor';
import {
    closeEditPageItemNameEditor,
    showEditPageItemNameEditorErrors
} from '../../actions/editPageItemNameEditor';
import {
    goToBannersEditForCreation,
    goToGroupsEdit,
    NoticeType,
    resetGridData,
    showNoticeMessage
} from '../../actions/grid-screen';
import {
    OPEN_GROUP_FILTER_INSPECTOR,
    OpenGroupFilterInspectorAction,
    setFilterFeedCategories,
    updateFeedFilterSchema
} from '../../actions/groupFilterInspector';
import {
    HANDLE_GROUPS_EDIT_MASS_ACTIONS,
    HandleGroupsEditMassActions,
    openMinusPhraseGroupsMassActions
} from '../../actions/groups-edit/groups-edit-mass-actions';
import {
    PERFORMANCE_SET_VISIBLE_GROUP_BANNER_POPUP_FEED,
    SetVisibleGroupBannerPopupFeedAction,
    updateFeeds
} from '../../actions/groups-edit/performance';
import {
    FETCH_RECOMMENDATIONS_FOR_GROUPS_EDIT,
    fetchRecommendationsForGroupsEdit,
    FetchRecommendationsForGroupsEditAction,
    OPEN_RECOMMENDATIONS_MODAL_FOR_GROUPS_EDIT,
    OpenRecommendationsModalForGroupsEditAction,
    updateRecommendationsForGroupsEdit
} from '../../actions/groups-edit/recommendations';
import {
    APPLY_SELECTOR_WIZARD_FOR_GROUP_EDIT,
    ApplySelectorWizardForGroupEdit,
    CANCEL_GROUP_EDITOR_REGIONS,
    CancelGroupEditorRegions,
    changeGroupEditPageStatus,
    changeGroupName,
    changeGroupRetargeting,
    clearErrorsGroupEditorRegions,
    CREATE_SMART_AD_GROUP,
    CREATE_TEXT_AD_GROUP,
    CreateSmartAdGroupAction,
    createTextAdGroup as createTextAdGroupAction,
    CreateTextAdGroupAction,
    deleteRetargetingConditionFromGroup,
    DeleteRetargetingConditionFromGroup,
    GET_CAMPAIGN_BASIC_DATA,
    GET_CPC_GROUPS_FOR_EDIT,
    GET_CPM_GROUPS_FOR_EDIT,
    GET_GROUPS_FOR_EDIT,
    GET_INTERNAL_GROUPS_FOR_EDIT,
    GET_RETARGETING_CONDITIONS_FOR_GROUPS_EDIT,
    GetCampaignBasicDataAction,
    GetCpcGroupsForEditAction,
    getCpcGroupsForEditAction,
    GetCpmGroupsForEditAction,
    getCpmGroupsForEditAction,
    GetGroupsForEditAction,
    getInternalGroupsForEditAction,
    GetInternalGroupsForEditAction,
    getRetargetingConditionsForGroupsEdit,
    GetRetargetingConditionsForGroupsEditAction,
    LEAVE_GROUPS_EDIT,
    LeaveGroupsEdit,
    moveNewGroupToCreated,
    OPEN_GROUP_RETARGETING_EDITOR,
    OPEN_PHRASES_SELECTOR_WIZARD_FOR_GROUPS_EDIT,
    OpenGroupRetargetingEditor,
    OpenPhrasesSelectorWizardForGroupsEdit,
    RESET_GROUPS_EDIT,
    ResetGroupsEdit,
    resetGroupsEdit,
    resetRetargetingErrors,
    SAVE_GROUP_EDITOR_REGIONS,
    SAVE_GROUP_MINUS_PHRASES_EDITOR,
    SAVE_GROUP_NAME_EDITOR_CHANGES,
    SAVE_GROUP_RATE_CORRECTIONS_EDITOR,
    SAVE_GROUP_RETARGETING_EDITOR,
    SAVE_GROUPS,
    saveGroupEditorRegions,
    SaveGroupEditorRegions,
    SaveGroupMinusPhrasesEditorAction,
    SaveGroupNameEditorChangesAction,
    SaveGroupRateCorrectionsEditorAction,
    SaveGroupRetargetingEditor,
    saveGroups,
    SaveGroupsAction,
    showGroupEditError,
    ShowGroupEditErrorAction,
    showGroupEditorRegionsErrors,
    showGroupsClientValidationErrors,
    showGroupsPartialSaveResult,
    UPDATE_GROUP_AUDIENCE_CRYPTA_AUDIO_GENRES,
    UPDATE_GROUP_AUDIENCE_CRYPTA_BEHAVIORAL,
    UPDATE_GROUP_AUDIENCE_CRYPTA_GROUP_FAMILY,
    UPDATE_GROUP_AUDIENCE_CRYPTA_SOCIAL_DEMO,
    UPDATE_GROUP_AUDIENCE_TARGETING_INTERESTS,
    UPDATE_GROUP_EDITOR_REGIONS,
    UPDATE_GROUP_TYPE,
    UPDATE_SHOW_CONDITION_TYPE,
    updateDataForEditGroups,
    UpdateGroupAudienceCryptaAudioGenres,
    updateGroupAudienceCryptaAudioGenres,
    UpdateGroupAudienceCryptaBehavioral,
    updateGroupAudienceCryptaBehavioral,
    UpdateGroupAudienceCryptaGroupFamily,
    updateGroupAudienceCryptaGroupFamily,
    updateGroupAudienceCryptaSocialDemo,
    UpdateGroupAudienceCryptaSocialDemo,
    updateGroupAudienceTargetingInterests,
    updateGroupEditorRegions,
    updateGroupIndoorMap,
    updateGroupMinusPhrases,
    updateGroupOutdoorMap,
    updateGroupRateCorrections,
    UpdateGroupTypeAction,
    UpdateProductRestrictionIdAction,
    updateProductRestrictionIdAction,
    updateProductsList,
    updateRetargetingConditions,
    updateRetargetingConditionsForEditGroups,
    UpdateRetargetingConditionsForEditGroupsAction,
    UpdateShowConditionTypeAction
} from '../../actions/groupsEdit';
import {
    SAVE_INTERNAL_GROUPS,
    SaveInternalGroupsAction
} from '../../actions/groupsEditInternal';
import {
    clearIndoorErrors,
    closeIndoorMapEditor,
    SAVE_INDOOR_MAP_ACTION,
    SaveIndoorMapAction
} from '../../actions/indoorMapEditor';
import {
    changeMinusPhrasesEditor,
    closeMinusPhrasesEditor,
    showEditPageMinusPhrasesEditorErrors
} from '../../actions/minusPhrasesEditor';
import {
    clearOutdoorErrors,
    closeOutdoorMapEditor,
    SAVE_OUTDOOR_MAP_ACTION,
    SaveOutdoorMapAction
} from '../../actions/outdoorMapEditor';
import {
    AddPhrasesStartInputItems,
    addPhrasesStartInputItems,
    closePhrasesSelectorWizard,
    OpenPhrasesSelectorWizard,
    openPhrasesSelectorWizard,
    UpdatePhrasesSelectorWizard
} from '../../actions/phrasesSelectorWizard';
import {
    ClearRateCorrectionsEditor,
    clearRateCorrectionsEditor,
    CloseRateCorrectionsEditor,
    closeRateCorrectionsEditor,
    resetRateCorrectionsEditors,
    updateRateCorrectionsEditorValidation
} from '../../actions/rateCorrectionsEditor';
import { setRecommendationsModalDataAction } from '../../actions/recommendations-modal';
import {
    cancelChangesRegionsEditor,
    closeRegionsEditor,
    ResetRegionsEditor,
    resetRegionsEditor
} from '../../actions/regionsEditor';
import {
    ADD_RETARGETING_LIST_ITEM,
    CHANGE_RETARGETING_LIST_ITEM_SELECTION,
    CHANGE_RETARGETING_LIST_SELECTION,
    ChangeRetargetingListItemSelection,
    ChangeRetargetingListSelection,
    closeRetargetingEditor,
    DELETE_RETARGETING_LIST_ITEM,
    DeleteRetargetingListItem,
    openRetargetingEditor,
    resetRetargetingEditors,
    UPDATE_RETARGETING_LIST_ITEM,
    UpdateRetargetingListItem
} from '../../actions/retargetingEditor';
import { updateTagCloudTargetings } from '../../actions/tagCloudTargeting';
import {
    closeCryptaAudioGenresEditor,
    SAVE_CRYPTA_AUDIO_GENRES,
    SaveCryptaAudioGenres
} from '../../actions/targetings/cryptaAudioGenres';
import {
    closeCryptaBehavioralEditor,
    SAVE_CRYPTA_BEHAVIORAL,
    SaveCryptaBehavioral
} from '../../actions/targetings/cryptaBehavioral';
import {
    closeCryptaGroupFamilyEditor,
    SAVE_CRYPTA_GROUP_FAMILY,
    SaveCryptaGroupFamily
} from '../../actions/targetings/cryptaGroupFamily';
import {
    closeCryptaSocialDemoEditor,
    SAVE_CRYPTA_SOCIAL_DEMO,
    SaveCryptaSocialDemo
} from '../../actions/targetings/cryptaSocialDemo';
import {
    closeInterestsTargetingEditor,
    SAVE_INTERESTS_TARGETING_SET,
    SaveInterestsTargetingSet
} from '../../actions/targetings/interestsEditor';
import { FetchMinusPhraseLibraryDataResult } from '../../api/MinusPhraseLibraryProvider/MinusPhraseLibraryProvider';
import { mapCampaignRowTypeToCampaignType } from '../../mappers/common/common';
import { mapCryptaSegmentsFromApiFormat } from '../../mappers/cryptaSegments/cryptaSegments';
import { mapCampaignTypeToCalcType } from '../../mappers/filter/filter';
import {
    getGroupsValueFromOpenedEditors,
    getInternalGroupsValueFromEditors,
    getNewGroupName,
    makeGroupsForEditNewCpmGroupData,
    makeGroupsForEditNewGroupData,
    mapCpmGroupsDataToApiFormat,
    mapFeedFilterSchemaFromApiResponse,
    mapFeeds,
    mapGroupEditRecommendationsToQueryResponse,
    mapGroupsDataToApiFormat,
    mapGroupsForEditQueryResultToQueryResponse,
    mapGroupToInternalInput,
    mapNewGroupToInternalInput,
    mapRetargetingConditionsQueryResultToQueryResponse
} from '../../mappers/group/group';
import {
    mapDatesIntervalEditorsDataFromInternalGroups,
    mapInternalGroupValueToTagCloudEditor
} from '../../mappers/internalGroup';
import { mapFetchResultToLibraryItem } from '../../mappers/minusPhrases';
import { mapPhraseToPhrasesSelectorListItem } from '../../mappers/phrasesSelectorWizard';
import {
    mapIndoorPlacementsMapFromApiFormat,
    mapOutdoorPlacementsMapFromApiFormat
} from '../../mappers/placementsMap/placementsMap';
import {
    getAllowedRateCorrectionsDataForGroup,
    mapEditorRateCorrectionsToRateCorrections,
    mapRatesCorrectionsConstantsFromApiFormat
} from '../../mappers/rateCorrections/rateCorrections';
import { isGroupSupportsRecommendationsOnEditBannerPage } from '../../mappers/recommendation';
import {
    mapRetargetingConditionFromApiFormat,
    mapRetargetingEditorValueToGroupRetargetingValue
} from '../../mappers/retargeting/retargeting';
import { awaitCommonData } from '../../operators';
import { getAllCryptaSegmentsByType } from '../../selectors/cryptaSegments/cryptaSegments';
import { selectEditPageItemNameEditorValue } from '../../selectors/editPageItemNameEditor/editPageItemNameEditor';
import {
    getGroupModalRecommendations,
    selectAccessRights,
    selectClientFeatures,
    selectEditableGroupTypes,
    selectGroupValidationParams,
    selectOperatorFeatures
} from '../../selectors/groupEdit/group/group';
import { filterGroupIndoorMapData } from '../../selectors/placementsMap/placementsMap';
import { BannersIdsForBannersEditHelpers } from '../../service/BannersIdsForBannersEditHelpers';
import { confirm } from '../../service/Confirm/Confirm';
import { isUserProfileEmpty } from '../../service/cpm/cpm';
import { calculateOpenedCategory } from '../../service/CryptaHelpers/CryptaHelpers';
import {
    ErrorOrWarning,
    extractUpdatedGroupIds,
    extractValidationErrors
} from '../../service/ErrorHelpers/ErrorHelpers';
import { ApiMethodExecutingError, NotSyncedError } from '../../service/Errors';
import {
    getGroupNameEditorValidationResult,
    getGroupsOpenedEditorsValidationResult
} from '../../service/GroupEditErrors/ClientGroupEditErrors/ClientGroupEditErrors';
import { ExternalGroupValidationSchema } from '../../service/GroupEditErrors/ExternalGroupValidationSchema/ExternalGroupValidationSchema';
import {
    createCpmGroupUpdateHandleErrorMapper,
    createInternalGroupAddHandleErrorMapper,
    createInternalGroupUpdateHandleErrorMapper,
    createSmartGroupAddHandleErrorMapper,
    createSmartGroupUpdateHandleErrorMapper,
    createTextGroupUpdateHandleErrorMapper
} from '../../service/GroupEditErrors/GroupsBackToFrontErrorMappers';
import { InternalGroupValidationSchema } from '../../service/GroupEditErrors/InternalGroupValidationSchema/InternalGroupValidationSchema';
import { GroupIdsForGroupsEditHelpers } from '../../service/GroupIdsForGroupsEditHelpers';
import { GroupsHelpers } from '../../service/GroupsHelpers/GroupsHelpers';
import { isInternalGroup } from '../../service/InternalGroupHelpers/InternalGroupHelpers';
import { LangUtils } from '../../service/Lang/Lang';
import { createTextAdGroup } from '../../service/mutationHelpers/mutationHelpers';
import { validateRateCorrectionEditor } from '../../service/RateCorrectionsEditorHelpers/RateCorrectionsEditorHelpers';
import { RegionsHelpers } from '../../service/RegionsHelpers/RegionsHelpers';
import { EpicDeps } from '../../service/StoreFactory';
import { UrlHelpers } from '../../service/UrlHelpers';
import { BannersScreenUrlSearchQueryParams } from '../../typings/banners';
import { ErrorsEdit, PageStatus, Region } from '../../typings/common';
import { CampaignsCalcTypeFilter, CampaignType } from '../../typings/domain';
import { DnaEpic, DnaEpicAction } from '../../typings/epic';
import {
    addInternalAdGroups,
    GdAddInternalAdGroupsInput,
    GdCampaignType,
    GdUpdateInternalAdGroupsInput,
    GroupsQueryForEdit_client_feeds_rowset,
    PlacementTypeInput,
    UpdateAdGroup,
    UpdateCpmAdGroup,
    updateInternalAdGroups
} from '../../typings/graphql-grid';
import { GridScreenUrlSearchQueryParams } from '../../typings/grid';
import {
    GroupId,
    GroupsEditMassActionItemType,
    GroupsScreenUrlSearchQueryParams
} from '../../typings/groups';
import {
    DataForEditInternalGroups,
    EditGroupData,
    MassActionId
} from '../../typings/groupsEditState';
import { PhrasesSelectorWizardId } from '../../typings/phrases';
import { RecommendationsModalState } from '../../typings/recommendations';
import { State } from '../../typings/state';

import * as keyset from './groupsEdit.i18n';

const i18n = i18nFactory(keyset);

export const updateShowConditionTypeEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = action$ => {
    return action$.pipe(
        ofType(UPDATE_SHOW_CONDITION_TYPE),
        mergeMap((action: UpdateShowConditionTypeAction) => {
            return [
                closeRateCorrectionsEditor(action.groupId),
                getRetargetingConditionsForGroupsEdit(action.ulogin),
                clearRateCorrectionsEditor(action.groupId)
            ];
        })
    );
};

export const initGroupsEditors = (
    groupsData: DataForEditInternalGroups
): Action[] => {
    const datesIntervalEditorsData = mapDatesIntervalEditorsDataFromInternalGroups(
        groupsData.groups
    );

    const tagCloudTargetingEditors = mapInternalGroupValueToTagCloudEditor(
        groupsData.groups
    );

    return compact([
        datesIntervalEditorsData &&
            updateDatesIntervalEditors(datesIntervalEditorsData),
        tagCloudTargetingEditors &&
            updateTagCloudTargetings(tagCloudTargetingEditors)
    ]);
};

export const getCampaignBasicDataEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(GET_CAMPAIGN_BASIC_DATA),
        awaitCommonData(action$, store$),
        mergeMap((action: GetCampaignBasicDataAction) => {
            const { campaignId, login } = action;
            const state = store$.value;
            const {
                commonData: {
                    client: { translocalGeotreeType, workCurrency }
                },
                editGroups: { isNew }
            } = state;
            const { isCpcDeviceModifiersAllowed } = selectClientFeatures(state);

            return forkJoin(
                deps.groupsDataProvider.getCampaignBasicDataForGroupEdit(
                    login,
                    campaignId
                ),
                RegionsHelpers.fetchRegions(),
                deps.groupsDataProvider.getRetargetingConditions(login)
            ).pipe(
                mergeMap(
                    ([
                        campaignResult,
                        regionsResult,
                        retargetingConditionsResult
                    ]): Observable<Action> | Array<Action> => {
                        const [
                            campaignData
                        ] = campaignResult.data.client.campaigns.rowset;

                        // DIRECT-99993 для текстовых кампаний создаем группу, нужно будет убрать в DIRECT-99992
                        if (campaignData.type === GdCampaignType.TEXT) {
                            return of(
                                createTextAdGroupAction(
                                    campaignId,
                                    getNewGroupName(),
                                    true
                                )
                            );
                        }
                        const isDeviceModifiersAllowed =
                            campaignData.type === GdCampaignType.CPM_BANNER ||
                            isCpcDeviceModifiersAllowed;
                        const {
                            allowedBidModifierTypesByAdGroupTypes,
                            allowedBidModifiersValues,
                            clientLimits
                        } = campaignResult.data.client.clientConstants;
                        const allowedRateCorrectionsData = getAllowedRateCorrectionsDataForGroup(
                            allowedBidModifierTypesByAdGroupTypes
                        );
                        const rateCorrectionsConstants = mapRatesCorrectionsConstantsFromApiFormat(
                            allowedBidModifiersValues,
                            Boolean(isDeviceModifiersAllowed)
                        );
                        const regionTree: Region[] = regionsResult
                            ? RegionsHelpers.getRegionsTree(
                                  regionsResult,
                                  LangUtils.getCurrentLang(),
                                  translocalGeotreeType
                              )
                            : [];
                        const retargetingConditions = retargetingConditionsResult.data.client.retargetingConditions.rowset.map(
                            retargetingCondition =>
                                mapRetargetingConditionFromApiFormat(
                                    retargetingCondition,
                                    login
                                )
                        );

                        // Для CPM_BANNER мы ходим за списком продуктов и по нему рисуем создание
                        if (campaignData.type === GdCampaignType.CPM_BANNER) {
                            return forkJoin(
                                deps.groupsDataProvider.getProducts(
                                    CampaignsCalcTypeFilter.CPM,
                                    CampaignType.CPM_BANNER,
                                    workCurrency
                                ),
                                deps.groupsDataProvider.getCryptaSegments(''),
                                deps.groupsDataProvider.getPlacements({
                                    placementType: PlacementTypeInput.OUTDOOR
                                }),
                                deps.groupsDataProvider.getPlacements({
                                    placementType: PlacementTypeInput.INDOOR
                                })
                            ).pipe(
                                mergeMap(
                                    ([
                                        productsList,
                                        cryptaSegmentsResult,
                                        placementsOutdoor,
                                        placementsIndoor
                                    ]): Array<Action> => {
                                        const cryptaSegments = mapCryptaSegmentsFromApiFormat(
                                            cryptaSegmentsResult
                                        );

                                        const cpmGroupsForEditData = makeGroupsForEditNewCpmGroupData(
                                            campaignData,
                                            regionTree,
                                            allowedRateCorrectionsData,
                                            clientLimits.keywordsCountLimit,
                                            productsList,
                                            rateCorrectionsConstants
                                        );

                                        return [
                                            updateOutdoorMapData(
                                                mapOutdoorPlacementsMapFromApiFormat(
                                                    placementsOutdoor
                                                )
                                            ),
                                            updateIndoorMapData(
                                                mapIndoorPlacementsMapFromApiFormat(
                                                    placementsIndoor
                                                )
                                            ),
                                            updateCryptaSegments(
                                                cryptaSegments
                                            ),
                                            updateProductsList(productsList),
                                            updateRegions(regionsResult),
                                            updateDataForEditGroups(
                                                cpmGroupsForEditData
                                            ),
                                            ...calculateOpenedCategory(
                                                cpmGroupsForEditData
                                            )
                                        ];
                                    }
                                )
                            );
                        }

                        // Для отображения пустой группы до ее создания в базе данных
                        // Необходимо только для смарт-групп, так как их создание состоит из нескольких шагов
                        const groupsForEditData = makeGroupsForEditNewGroupData(
                            campaignData,
                            regionTree,
                            retargetingConditions,
                            allowedRateCorrectionsData,
                            rateCorrectionsConstants,
                            clientLimits.keywordsCountLimit
                        );

                        // FIXME: Оторвать внутренние группы от внешних
                        //        @alex-sannikov
                        //        https://st.yandex-team.ru/DIRECT-100081
                        if (isNew && isInternalGroup(campaignData.type)) {
                            return [
                                updateRegions(regionsResult),
                                ...initGroupsEditors(
                                    groupsForEditData as DataForEditInternalGroups
                                ),
                                updateDataForEditGroups(groupsForEditData)
                            ];
                        }

                        return [
                            updateRegions(regionsResult),
                            updateDataForEditGroups(groupsForEditData)
                        ];
                    }
                ),
                catchError((error: ApiMethodExecutingError) =>
                    of(showGroupEditError(error))
                )
            );
        })
    );
};

export const getGroupsForEditEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    const { login } = store$.value.commonData;

    return action$.pipe(
        ofType(GET_GROUPS_FOR_EDIT),
        awaitCommonData(action$, store$),
        mergeMap(
            (action: GetGroupsForEditAction): Observable<Action> => {
                const state = store$.value;
                const {
                    editGroups: { groupsIds, campaignsIds }
                } = state;
                const { accessRights } = state.commonData;

                if (groupsIds.length === 0 || campaignsIds.length === 0) {
                    const url = UrlHelpers.getGroupsListUrl();
                    return of(
                        changeLocation(url, {
                            ulogin: login
                        })
                    );
                }

                return forkJoin(
                    deps.groupsDataProvider.getGroups(
                        action.login,
                        groupsIds,
                        campaignsIds
                    ),
                    RegionsHelpers.fetchRegions(),
                    deps.groupsDataProvider.getUaTraits(
                        state.commonData.stringDicts,
                        accessRights && accessRights.canEditInternalAds
                    )
                ).pipe(
                    mergeMap(([groupsData, regionsData, stringDictsResult]) => {
                        const {
                            data: {
                                client: {
                                    adGroups: {
                                        rowset: [
                                            {
                                                campaign: { type }
                                            }
                                        ]
                                    }
                                }
                            }
                        } = groupsData;

                        let groupsForEditAction;

                        // FIXME: Оторвать внутренние группы от внешних
                        //        @alex-sannikov
                        //        https://st.yandex-team.ru/DIRECT-100081
                        if (isInternalGroup(type)) {
                            groupsForEditAction = getInternalGroupsForEditAction;
                        } else {
                            groupsForEditAction =
                                mapCampaignTypeToCalcType(
                                    mapCampaignRowTypeToCampaignType(type)
                                ) === CampaignsCalcTypeFilter.CPM
                                    ? getCpmGroupsForEditAction
                                    : getCpcGroupsForEditAction;
                        }

                        return [
                            resetRegionsEditor(),
                            updateRegions(regionsData),
                            updateStringDicts(stringDictsResult),
                            groupsForEditAction(login, groupsData)
                        ];
                    }),
                    catchError((error: ApiMethodExecutingError) => {
                        return of(showGroupEditError(error));
                    })
                );
            }
        )
    );
};

export const updateGroupTypeEpic: DnaEpic = action$ => {
    return action$.pipe(
        ofType(UPDATE_GROUP_TYPE),
        mergeMap(
            ({
                groupId,
                group
            }: UpdateGroupTypeAction): [
                CloseRateCorrectionsEditor,
                UpdateProductRestrictionIdAction,
                ClearRateCorrectionsEditor
            ] => [
                closeRateCorrectionsEditor(groupId),
                updateProductRestrictionIdAction(groupId, group.restrictionId),
                clearRateCorrectionsEditor(groupId)
            ]
        )
    );
};

// FIXME: Оторвать внутренние группы от внешних
//        @alex-sannikov
//        https://st.yandex-team.ru/DIRECT-100081
export const getInternalGroupsForEditEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(GET_INTERNAL_GROUPS_FOR_EDIT),
        mergeMap((action: GetInternalGroupsForEditAction) => {
            const state = store$.value;
            const { groupsData } = action;
            const {
                commonData: { client, regions }
            } = state;

            const regionTree: Region[] = regions
                ? RegionsHelpers.getRegionsTree(
                      regions,
                      LangUtils.getCurrentLang(),
                      client.translocalGeotreeType
                  )
                : [];

            const groupsForEditData = mapGroupsForEditQueryResultToQueryResponse(
                action.login,
                groupsData,
                regionTree,
                false
            );

            return [
                ...initGroupsEditors(
                    groupsForEditData as DataForEditInternalGroups
                ),
                updateDataForEditGroups(groupsForEditData)
            ];
        })
    );
};

export const getCpcGroupsForEditEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(GET_CPC_GROUPS_FOR_EDIT),
        mergeMap((action: GetCpcGroupsForEditAction) => {
            const state = store$.value;
            const { groupsData } = action;
            const { isCpcDeviceModifiersAllowed } = selectClientFeatures(state);
            const {
                commonData: { client, regions }
            } = state;

            const regionTree: Region[] = regions
                ? RegionsHelpers.getRegionsTree(
                      regions,
                      LangUtils.getCurrentLang(),
                      client.translocalGeotreeType
                  )
                : [];

            const groupsForEditData = mapGroupsForEditQueryResultToQueryResponse(
                action.login,
                groupsData,
                regionTree,
                Boolean(isCpcDeviceModifiersAllowed)
            );

            return [updateDataForEditGroups(groupsForEditData)];
        })
    );
};

export const getCpmGroupsForEditEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    const { login } = store$.value.commonData;

    return action$.pipe(
        ofType(GET_CPM_GROUPS_FOR_EDIT),
        mergeMap(
            (action: GetCpmGroupsForEditAction): Observable<Action> => {
                const state = store$.value;
                const { groupsData } = action;

                const {
                    client: { retargetings }
                } = groupsData.data;

                const {
                    editGroups: { groupsIds, campaignsIds },
                    commonData: {
                        client: { workCurrency }
                    }
                } = state;

                if (groupsIds.length === 0 || campaignsIds.length === 0) {
                    const url = UrlHelpers.getGroupsListUrl();
                    return of(
                        changeLocation(url, {
                            ulogin: login
                        })
                    );
                }

                return forkJoin(
                    deps.groupsDataProvider.getProducts(
                        CampaignsCalcTypeFilter.CPM,
                        CampaignType.CPM_BANNER,
                        workCurrency
                    ),
                    deps.groupsDataProvider.getRetargetingConditionsForCpm(
                        login,
                        retargetings.rowset.map(
                            ret => ret.retargetingConditionId
                        )
                    )
                ).pipe(
                    mergeMap(([productsList, userProfileRetargetings]) => {
                        const {
                            commonData: { client, regions }
                        } = state;

                        const regionTree: Region[] = regions
                            ? RegionsHelpers.getRegionsTree(
                                  regions,
                                  LangUtils.getCurrentLang(),
                                  client.translocalGeotreeType
                              )
                            : [];

                        const groupsForEditData = mapGroupsForEditQueryResultToQueryResponse(
                            action.login,
                            groupsData,
                            regionTree,
                            true,
                            userProfileRetargetings,
                            productsList
                        );

                        const {
                            fetchIndoorPlacements,
                            fetchOutdoorPlacements,
                            fetchCrypta
                        } = GroupsHelpers.getFetchFlags(
                            groupsForEditData,
                            productsList
                        );
                        const emptyResponse = Promise.resolve(undefined);

                        return forkJoin(
                            fetchOutdoorPlacements
                                ? deps.groupsDataProvider.getPlacements({
                                      placementType: PlacementTypeInput.OUTDOOR
                                  })
                                : emptyResponse,
                            fetchIndoorPlacements
                                ? deps.groupsDataProvider.getPlacements({
                                      placementType: PlacementTypeInput.INDOOR
                                  })
                                : emptyResponse,
                            fetchCrypta
                                ? deps.groupsDataProvider.getCryptaSegments('')
                                : emptyResponse
                        ).pipe(
                            mergeMap(
                                ([
                                    placementsOutdoor,
                                    placementsIndoor,
                                    cryptaSegmentsResult
                                ]) => {
                                    const additiveActions = [];

                                    if (placementsOutdoor) {
                                        additiveActions.push(
                                            updateOutdoorMapData(
                                                mapOutdoorPlacementsMapFromApiFormat(
                                                    placementsOutdoor
                                                )
                                            )
                                        );
                                    }

                                    if (placementsIndoor) {
                                        additiveActions.push(
                                            updateIndoorMapData(
                                                mapIndoorPlacementsMapFromApiFormat(
                                                    placementsIndoor
                                                )
                                            )
                                        );
                                    }

                                    if (cryptaSegmentsResult) {
                                        additiveActions.push(
                                            updateCryptaSegments(
                                                mapCryptaSegmentsFromApiFormat(
                                                    cryptaSegmentsResult
                                                )
                                            )
                                        );
                                    }

                                    const groupListValues = values(
                                        groupsForEditData.groups
                                    );

                                    const groupSupportsRecommendationIds = groupListValues
                                        .filter(value =>
                                            isGroupSupportsRecommendationsOnEditBannerPage(
                                                value.value.groupType
                                            )
                                        )
                                        .map(group => group.id);

                                    const getRecommendationAction = groupSupportsRecommendationIds.length
                                        ? [
                                              fetchRecommendationsForGroupsEdit(
                                                  login,
                                                  campaignsIds,
                                                  groupSupportsRecommendationIds
                                              )
                                          ]
                                        : [];

                                    return [
                                        ...additiveActions,
                                        ...getRecommendationAction,
                                        updateProductsList(productsList),
                                        updateDataForEditGroups(
                                            groupsForEditData
                                        ),
                                        ...calculateOpenedCategory(
                                            groupsForEditData
                                        )
                                    ];
                                }
                            )
                        );
                    }),
                    catchError((error: ApiMethodExecutingError) => {
                        return of(showGroupEditError(error));
                    })
                );
            }
        )
    );
};

export const getRecommendationsForGroupsEditEpic: DnaEpic = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(FETCH_RECOMMENDATIONS_FOR_GROUPS_EDIT),
        mergeMap(
            (
                action: FetchRecommendationsForGroupsEditAction
            ): Observable<Action> => {
                return from(
                    deps.groupsDataProvider.getGroupEditRecommendations(
                        action.login,
                        action.campainIds,
                        action.groupIds
                    )
                ).pipe(
                    map(
                        (data): Action => {
                            return updateRecommendationsForGroupsEdit(
                                mapGroupEditRecommendationsToQueryResponse(data)
                            );
                        }
                    ),
                    catchError(
                        (
                            error: ApiMethodExecutingError
                        ): Observable<Action> => {
                            return of(showGroupEditError(error));
                        }
                    )
                );
            }
        )
    );
};

export const openRecommendationsModalForGroupsEditEpic: DnaEpic = (
    action$,
    store$
) => {
    return action$.pipe(
        ofType(OPEN_RECOMMENDATIONS_MODAL_FOR_GROUPS_EDIT),
        map(
            (action: OpenRecommendationsModalForGroupsEditAction): Action => {
                const state = store$.value;
                const modalData: RecommendationsModalState = getGroupModalRecommendations(
                    state,
                    action.id
                );

                return setRecommendationsModalDataAction(modalData);
            }
        )
    );
};

export const getRetargetingConditionsForGroupsEditEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(GET_RETARGETING_CONDITIONS_FOR_GROUPS_EDIT),
        awaitCommonData(action$, store$),
        mergeMap(
            (
                action: GetRetargetingConditionsForGroupsEditAction
            ): Observable<UpdateRetargetingConditionsForEditGroupsAction> => {
                return from(
                    deps.groupsDataProvider.getRetargetingConditions(
                        action.login
                    )
                ).pipe(
                    mergeMap(retargetingResult => {
                        const retargetingConditionsForGroupsEdit = mapRetargetingConditionsQueryResultToQueryResponse(
                            action.login,
                            retargetingResult
                        );

                        return [
                            updateRetargetingConditionsForEditGroups(
                                retargetingConditionsForGroupsEdit
                            )
                        ];
                    })
                );
            }
        )
    );
};

export const saveGroupsEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    const processResponse = (
        response: FetchResult<UpdateAdGroup>,
        groupsData: Record<GroupId, EditGroupData>,
        textGroupIds: Array<GroupId>,
        smartGroupIds: Array<GroupId>,
        withRedirect: boolean,
        thenCreateBanners: boolean,
        campaignId: string
    ): Observable<Action> => {
        const {
            commonData: { login, fromOldInterface }
        } = store$.value;
        let resultActions: Array<Action> = [];

        let groupsDataToSave = groupsData;

        if (response.errors && response.errors.length) {
            resultActions = [showGroupEditError(response.errors[0])];
        } else {
            if (!response.data) {
                throw new Error('internal server error');
            }

            const { updateTextAdGroup, updateSmartAdGroups } = response.data;

            const [
                hasTextValidationErrors,
                textServerErrors
            ] = extractValidationErrors(updateTextAdGroup);
            const [
                hasSmartValidationErrors,
                smartServerErrors
            ] = extractValidationErrors(updateSmartAdGroups);

            // эту функцию я упер с сайта lodash
            // она нужна, чтобы при слиянии двух объектов массивы тоже сливались
            // без кастомайзера массивы перезаписывают друг друга
            const customizer = (
                objValue: Object,
                srcValue: Object
            ): Object | undefined => {
                if (isArray(objValue)) {
                    return objValue.concat(srcValue);
                }
                return undefined;
            };

            let validationErrors: ErrorsEdit = {};

            if (hasTextValidationErrors) {
                const textGroupUpdateHandleErrorMapper = createTextGroupUpdateHandleErrorMapper(
                    textGroupIds
                );
                const textValidationErrors = textGroupUpdateHandleErrorMapper.mapServerErrorsToClientError(
                    textServerErrors
                );

                validationErrors = mergeWith(
                    validationErrors,
                    textValidationErrors,
                    customizer
                );
            }

            if (hasSmartValidationErrors) {
                const smartGroupUpdateHandleErrorMapper = createSmartGroupUpdateHandleErrorMapper(
                    smartGroupIds
                );
                const smartValidationErrors = smartGroupUpdateHandleErrorMapper.mapServerErrorsToClientError(
                    smartServerErrors
                );

                validationErrors = mergeWith(
                    validationErrors,
                    smartValidationErrors,
                    customizer
                );
            }

            const hasErrors =
                hasTextValidationErrors || hasSmartValidationErrors;

            if (!hasErrors && withRedirect) {
                let changeLocationAction: Action;

                if (thenCreateBanners) {
                    const groupId = textGroupIds.concat(smartGroupIds)[0];
                    changeLocationAction = goToBannersEditForCreation(
                        login,
                        campaignId,
                        groupId
                    );
                } else {
                    if (fromOldInterface) {
                        changeLocationAction = changeLocationHard(
                            UrlHelpers.getShowCampaignUrl(campaignId, login)
                        );
                    } else {
                        changeLocationAction = changeLocation(
                            UrlHelpers.getGroupsListUrl(),
                            {
                                ulogin: login,
                                [GridScreenUrlSearchQueryParams.calcType]:
                                    CampaignsCalcTypeFilter.CPC
                            }
                        );
                    }
                }

                resultActions = [changeLocationAction];
            }

            const updatedGroupIds: GroupId[] = [
                ...extractUpdatedGroupIds(updateTextAdGroup),
                ...extractUpdatedGroupIds(updateSmartAdGroups)
            ];

            if (!withRedirect) {
                // в случае, если экшен затребован без редиректа (при создании смарт-группы)
                // нужно обновить serverValue, чтобы не было "несохраненных изменений"
                groupsDataToSave = GroupsHelpers.markEditGroupsAsSaved(
                    groupsDataToSave,
                    updatedGroupIds
                );
            }

            if (hasErrors) {
                const editorsResultActions$ = getShowEditorsErrorsFromServerActions(
                    validationErrors
                );

                resultActions = [
                    showGroupsPartialSaveResult({
                        errors: validationErrors,
                        updatedGroupIds
                    }),
                    ...editorsResultActions$
                ];
            }
        }

        return from([
            updateDataForEditGroups(
                {
                    groups: groupsDataToSave
                },
                true
            ),
            ...resultActions
        ]);
    };

    return action$
        .ofType(SAVE_GROUPS)
        .filter(() => {
            const {
                editGroups: { groups }
            } = store$.value;
            return some(
                groups,
                ({ value: { campaignType } }) =>
                    campaignType !== CampaignType.CPM_BANNER
            );
        })
        .mergeMap(
            (action: SaveGroupsAction): Observable<Action> => {
                const state = store$.value;
                const {
                    editGroups: { groups, isNewEmptyGroup, campaignsIds }
                } = state;
                const accessRights = selectAccessRights(state);
                const operatorFeatures = selectOperatorFeatures(state);
                const editableTypes = selectEditableGroupTypes(
                    operatorFeatures,
                    accessRights
                );
                const campaignId = campaignsIds[0];
                const {
                    login,
                    regions,
                    client: { workCurrency, translocalGeotreeType },
                    constants
                } = state.commonData;
                const regionTree: Region[] = regions
                    ? RegionsHelpers.getRegionsTree(
                          regions,
                          LangUtils.getCurrentLang(),
                          translocalGeotreeType
                      )
                    : [];

                const groupsData = getGroupsValueFromOpenedEditors(
                    groups,
                    state.rateCorrectionsEditor,
                    state.regionsEditor,
                    state.retargetingEditor,
                    state.minusPhraseLibraryEditor,
                    state.editPageItemNameEditor,
                    state.interestsTargetingEditor,
                    state.cryptaSocialDemoEditor,
                    state.cryptaGroupFamilyEditor,
                    state.cryptaAudioGenresEditor,
                    state.cryptaBehavioralEditor,
                    regionTree,
                    editableTypes
                );

                if (campaignId === undefined) {
                    throw new Error('не задан номер кампании');
                }

                const [
                    textInput,
                    smartInput,
                    textGroupIds,
                    smartGroupIds
                ] = mapGroupsDataToApiFormat(groupsData);

                if (constants) {
                    const openedEditorsValidationResult = getGroupsOpenedEditorsValidationResult(
                        values(groupsData),
                        state
                    );

                    const clientValidationErrors = new ExternalGroupValidationSchema().validate(
                        values(groupsData),
                        {
                            minGeneralPrice:
                                constants.currencyConstants[workCurrency]
                                    .minPrice,
                            maxGeneralPrice:
                                constants.currencyConstants[workCurrency]
                                    .maxPrice,
                            currency: workCurrency
                        }
                    );
                    let isValid = true;
                    let showErrorsActions: Array<Observable<Action>> = [];

                    if (Object.keys(clientValidationErrors).length !== 0) {
                        isValid = false;

                        showErrorsActions.push(
                            Observable.of(
                                showGroupsClientValidationErrors(
                                    clientValidationErrors
                                )
                            )
                        );
                    }

                    if (openedEditorsValidationResult.valid === false) {
                        isValid = false;

                        showErrorsActions = [
                            ...showErrorsActions,
                            ...openedEditorsValidationResult.showErrorsActions
                        ];
                    }

                    if (isValid === false) {
                        return Observable.concat(
                            Observable.of(
                                changeGroupEditPageStatus(PageStatus.LOADED)
                            ),
                            ...showErrorsActions
                        );
                    }
                }

                return Observable.fromPromise(
                    deps.groupsMutationExecutor.updateGroups(
                        textInput,
                        smartInput,
                        login
                    )
                )
                    .mergeMap(response =>
                        processResponse(
                            response,
                            groupsData,
                            textGroupIds,
                            smartGroupIds,
                            action.withRedirect,
                            isNewEmptyGroup,
                            campaignId
                        )
                    )
                    .catch(
                        (
                            error:
                                | ApiMethodExecutingError
                                | NotSyncedError<FetchResult<UpdateAdGroup>>
                        ): Observable<Action> => {
                            if (error instanceof NotSyncedError) {
                                // для NotSynced обрабатываеи результат, так же как для обычного ответа
                                return processResponse(
                                    error.data,
                                    groupsData,
                                    textGroupIds,
                                    smartGroupIds,
                                    false,
                                    isNewEmptyGroup,
                                    campaignId
                                );
                            }
                            return of(showGroupEditError(error));
                        }
                    );
            }
        );
};

export const saveCpmGroupsEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    const processCpmResponse = (
        response: FetchResult<UpdateCpmAdGroup & { notSynced?: boolean }>,
        groupsData: Record<GroupId, EditGroupData>,
        cpmGroupIds: Array<GroupId>,
        withRedirect: boolean,
        thenCreateBanners: boolean,
        campaignId: string
    ): Observable<Action> => {
        const {
            commonData: { login, fromOldInterface }
        } = store$.value;
        let resultActions: Array<Action> = [];

        let groupsDataToSave = groupsData;

        if (response.errors && response.errors.length) {
            resultActions = [showGroupEditError(response.errors[0])];
        } else {
            if (!response.data) {
                throw new Error('internal server error');
            }

            const { saveCpmAdGroups } = response.data;

            const [
                hasCpmValidationErrors,
                cpmServerErrors
            ] = extractValidationErrors(saveCpmAdGroups);

            // эту функцию я упер с сайта lodash
            // она нужна, чтобы при слиянии двух объектов массивы тоже сливались
            // без кастомайзера массивы перезаписывают друг друга
            const customizer = (
                objValue: Object,
                srcValue: Object
            ): Object | undefined => {
                if (isArray(objValue)) {
                    return objValue.concat(srcValue);
                }
                return undefined;
            };

            let validationErrors: ErrorsEdit = {};

            if (hasCpmValidationErrors) {
                const cpmGroupUpdateHandleErrorMapper = createCpmGroupUpdateHandleErrorMapper(
                    cpmGroupIds
                );
                const cpmValidationErrors = cpmGroupUpdateHandleErrorMapper.mapServerErrorsToClientError(
                    cpmServerErrors
                );

                validationErrors = mergeWith(
                    validationErrors,
                    cpmValidationErrors,
                    customizer
                );
            }

            if (!hasCpmValidationErrors && withRedirect) {
                let changeLocationAction: Action;

                if (thenCreateBanners) {
                    const groupId = get(
                        saveCpmAdGroups,
                        'updatedAdGroupItems[0].adGroupId',
                        cpmGroupIds[0]
                    );
                    changeLocationAction = goToBannersEditForCreation(
                        login,
                        campaignId,
                        String(groupId)
                    );
                } else {
                    if (fromOldInterface) {
                        changeLocationAction = changeLocationHard(
                            UrlHelpers.getShowCampaignUrl(campaignId, login)
                        );
                    } else {
                        changeLocationAction = changeLocation(
                            UrlHelpers.getGroupsListUrl(),
                            {
                                ulogin: login,
                                [GridScreenUrlSearchQueryParams.calcType]:
                                    CampaignsCalcTypeFilter.CPM
                            }
                        );
                    }
                }

                resultActions = [changeLocationAction];
            }

            const updatedGroupIds: GroupId[] = extractUpdatedGroupIds(
                saveCpmAdGroups
            );

            if (!withRedirect) {
                // в случае, если экшен затребован без редиректа (при создании смарт-группы)
                // нужно обновить serverValue, чтобы не было "несохраненных изменений"
                groupsDataToSave = GroupsHelpers.markEditGroupsAsSaved(
                    groupsDataToSave,
                    updatedGroupIds
                );
            }

            if (hasCpmValidationErrors) {
                const editorsResultActions$ = getShowEditorsErrorsFromServerActions(
                    validationErrors
                );

                resultActions = [
                    showGroupsPartialSaveResult({
                        errors: validationErrors,
                        updatedGroupIds
                    }),
                    ...editorsResultActions$
                ];
            }
        }

        return from([
            updateDataForEditGroups(
                {
                    groups: groupsDataToSave
                },
                true
            ),
            ...resultActions
        ]);
    };

    return action$
        .ofType(SAVE_GROUPS)
        .filter(() => {
            const {
                editGroups: { groups }
            } = store$.value;
            return some(
                groups,
                ({ value: { campaignType } }) =>
                    campaignType === CampaignType.CPM_BANNER
            );
        })
        .mergeMap(
            (action: SaveGroupsAction): Observable<Action> => {
                const state = store$.value;
                const { editGroups } = state;
                const {
                    groups,
                    isNewEmptyGroup,
                    campaignsIds,
                    productsList
                } = editGroups;
                const campaignId = campaignsIds[0];
                const {
                    login,
                    regions,
                    client: { workCurrency, translocalGeotreeType },
                    features: { isEmptyRetCondInCpmGroupEnabled },
                    constants
                } = state.commonData;
                const accessRights = selectAccessRights(state);
                const operatorFeatures = selectOperatorFeatures(state);
                const editableTypes = selectEditableGroupTypes(
                    operatorFeatures,
                    accessRights
                );
                const cryptaSegmentsByType = getAllCryptaSegmentsByType(state);
                const regionTree: Region[] = regions
                    ? RegionsHelpers.getRegionsTree(
                          regions,
                          LangUtils.getCurrentLang(),
                          translocalGeotreeType
                      )
                    : [];
                const groupsData = getGroupsValueFromOpenedEditors(
                    groups,
                    state.rateCorrectionsEditor,
                    state.regionsEditor,
                    state.retargetingEditor,
                    state.minusPhraseLibraryEditor,
                    state.editPageItemNameEditor,
                    state.interestsTargetingEditor,
                    state.cryptaSocialDemoEditor,
                    state.cryptaGroupFamilyEditor,
                    state.cryptaAudioGenresEditor,
                    state.cryptaBehavioralEditor,
                    regionTree,
                    editableTypes
                );

                if (campaignId === undefined) {
                    throw new Error('не задан номер кампании');
                }

                if (productsList === undefined) {
                    throw new Error('не получен список продуктов');
                }

                const [cpmInput, cpmGroupsIds] = mapCpmGroupsDataToApiFormat(
                    groupsData,
                    productsList,
                    cryptaSegmentsByType
                );

                if (constants) {
                    const openedEditorsValidationResult = getGroupsOpenedEditorsValidationResult(
                        values(groupsData),
                        state
                    );

                    const clientValidationErrors = new ExternalGroupValidationSchema().validate(
                        values(groupsData),
                        {
                            minGeneralPrice:
                                constants.currencyConstants[workCurrency]
                                    .minCpmPrice,
                            maxGeneralPrice:
                                constants.currencyConstants[workCurrency]
                                    .maxCpmPrice,
                            currency: workCurrency
                        }
                    );
                    let isValid = true;
                    let showErrorsActions: Array<Observable<Action>> = [];

                    if (Object.keys(clientValidationErrors).length !== 0) {
                        isValid = false;

                        showErrorsActions.push(
                            Observable.of(
                                showGroupsClientValidationErrors(
                                    clientValidationErrors
                                )
                            )
                        );
                    }

                    if (openedEditorsValidationResult.valid === false) {
                        isValid = false;

                        showErrorsActions = [
                            ...showErrorsActions,
                            ...openedEditorsValidationResult.showErrorsActions
                        ];
                    }

                    if (isValid === false) {
                        return Observable.concat(
                            Observable.of(
                                changeGroupEditPageStatus(PageStatus.LOADED)
                            ),
                            ...showErrorsActions
                        );
                    }
                }

                if (
                    isUserProfileEmpty(groupsData, cryptaSegmentsByType) &&
                    isEmptyRetCondInCpmGroupEnabled
                ) {
                    return from(
                        confirm({
                            message: i18n(
                                'warning-about-empty-retargeting-conditions'
                            )
                        })
                    ).pipe(
                        mergeMap(() => {
                            return Observable.fromPromise(
                                deps.groupsMutationExecutor.updateCpmGroups(
                                    cpmInput,
                                    login
                                )
                            )
                                .mergeMap(response =>
                                    processCpmResponse(
                                        response,
                                        groupsData,
                                        cpmGroupsIds,
                                        action.withRedirect,
                                        isNewEmptyGroup,
                                        campaignId
                                    )
                                )
                                .catch(
                                    (
                                        error:
                                            | ApiMethodExecutingError
                                            | NotSyncedError<
                                                  FetchResult<UpdateCpmAdGroup>
                                              >
                                    ): Observable<Action> => {
                                        if (error instanceof NotSyncedError) {
                                            // для NotSynced обрабатываеи результат, так же как для обычного ответа
                                            return processCpmResponse(
                                                error.data,
                                                groupsData,
                                                cpmGroupsIds,
                                                false,
                                                isNewEmptyGroup,
                                                campaignId
                                            );
                                        }
                                        return of(showGroupEditError(error));
                                    }
                                );
                        }),
                        catchError(
                            (): Observable<Action> =>
                                of(changeGroupEditPageStatus(PageStatus.LOADED))
                        )
                    );
                }

                return Observable.fromPromise(
                    deps.groupsMutationExecutor.updateCpmGroups(cpmInput, login)
                )
                    .mergeMap(response =>
                        processCpmResponse(
                            response,
                            groupsData,
                            cpmGroupsIds,
                            action.withRedirect,
                            isNewEmptyGroup,
                            campaignId
                        )
                    )
                    .catch(
                        (
                            error:
                                | ApiMethodExecutingError
                                | NotSyncedError<FetchResult<UpdateCpmAdGroup>>
                        ): Observable<Action> => {
                            if (error instanceof NotSyncedError) {
                                // для NotSynced обрабатываеи результат, так же как для обычного ответа
                                return processCpmResponse(
                                    error.data,
                                    groupsData,
                                    cpmGroupsIds,
                                    false,
                                    isNewEmptyGroup,
                                    campaignId
                                );
                            }
                            return of(showGroupEditError(error));
                        }
                    );
            }
        );
};

const getShowEditorsErrorsFromServerActions = (
    errorsInfo: ErrorsEdit
): Action[] => {
    const resultActions: Action[] = [];

    if (errorsInfo.groups) {
        forEach(errorsInfo.groups, (groupErrors, groupId) => {
            if (groupErrors.name && groupErrors.name.errors) {
                resultActions.push(
                    showEditPageItemNameEditorErrors(
                        GroupsHelpers.getGroupEditPageItemEditorId(groupId),
                        groupErrors.name.errors
                    )
                );
            }

            const minusKeywordsErrors =
                groupErrors.minusKeywords && groupErrors.minusKeywords.errors;
            const libraryMinusKeywordsIdsErrors =
                groupErrors.libraryMinusKeywordsIds &&
                groupErrors.libraryMinusKeywordsIds.errors;

            if (minusKeywordsErrors || libraryMinusKeywordsIdsErrors) {
                resultActions.push(
                    showEditPageMinusPhrasesEditorErrors(
                        groupId,
                        minusKeywordsErrors || [],
                        libraryMinusKeywordsIdsErrors || []
                    )
                );
            }
        });
    }

    return resultActions;
};

export const saveInternalGroupsEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    const processResponse = (
        response:
            | FetchResult<updateInternalAdGroups>
            | FetchResult<addInternalAdGroups>,
        groupIds: Array<GroupId>,
        isNew: boolean,
        withRedirect: boolean,
        campaignId: string
    ): Observable<Action> => {
        const {
            commonData: { login }
        } = store$.value;
        if (response.errors && response.errors.length) {
            return of(showGroupEditError(response.errors[0]));
        }
        let hasErrors;
        let serverErrors: Array<ErrorOrWarning>;
        let errors: ErrorsEdit;
        let updatedGroupIds: Array<GroupId>;

        if (isNew) {
            const resp = response as FetchResult<addInternalAdGroups>;
            if (!resp.data) {
                throw new Error('internal server error');
            }

            [hasErrors, serverErrors] = extractValidationErrors(
                resp.data.addInternalAdGroups
            );

            errors = createInternalGroupAddHandleErrorMapper(
                groupIds
            ).mapServerErrorsToClientError(serverErrors);
            updatedGroupIds = [];
        } else {
            const resp = response as FetchResult<updateInternalAdGroups>;
            if (!resp.data) {
                throw new Error('internal server error');
            }

            [hasErrors, serverErrors] = extractValidationErrors(
                resp.data.updateInternalAdGroups
            );

            errors = createInternalGroupUpdateHandleErrorMapper(
                groupIds
            ).mapServerErrorsToClientError(serverErrors);
            updatedGroupIds = extractUpdatedGroupIds(
                resp.data.updateInternalAdGroups
            );
        }

        if (hasErrors) {
            const editorsResultActions = getShowEditorsErrorsFromServerActions(
                errors
            );

            return of(
                showGroupsPartialSaveResult({
                    updatedGroupIds,
                    errors
                }),
                ...editorsResultActions
            );
        }

        if (isNew) {
            const resp = response as FetchResult<addInternalAdGroups>;

            const { adGroupId: groupId = undefined } =
                (resp.data &&
                    resp.data.addInternalAdGroups.addedAdGroupItems[0]) ||
                {};

            if (!groupId) {
                throw new Error('Something went wrong with group id');
            }

            BannersIdsForBannersEditHelpers.setCampAndGroupIdsForCreation(
                login,
                [campaignId],
                [String(groupId)]
            );

            return from([
                changeLocation(
                    UrlHelpers.getInternalBannersEditUrl({
                        [BannersScreenUrlSearchQueryParams.ulogin]: login,
                        [BannersScreenUrlSearchQueryParams.campaignsIds]: [
                            campaignId
                        ],
                        [BannersScreenUrlSearchQueryParams.groupsIds]: [
                            groupId
                        ],
                        [BannersScreenUrlSearchQueryParams.isNew]: '1'
                    }),
                    undefined,
                    false
                ),
                resetGridData()
            ]);
        }

        if (withRedirect) {
            return of(
                changeLocation(UrlHelpers.getGroupsListUrl(), {
                    ulogin: login
                })
            );
        }

        return EMPTY;
    };

    return action$.ofType(SAVE_INTERNAL_GROUPS).mergeMap(
        (action: SaveInternalGroupsAction): Observable<Action> => {
            const state = store$.value;
            const {
                editGroups: { groups, isNew, campaignsIds }
            } = state;
            const {
                regions,
                client: { translocalGeotreeType },
                login
            } = state.commonData;
            let isValid = true;
            let showErrorsActions: Array<Observable<Action>> = [];

            const campaignId = campaignsIds[0];

            if (campaignId === undefined) {
                throw new Error('не задан номер кампании');
            }

            const regionTree: Region[] = regions
                ? RegionsHelpers.getRegionsTree(
                      regions,
                      LangUtils.getCurrentLang(),
                      translocalGeotreeType
                  )
                : [];

            const groupsData = getInternalGroupsValueFromEditors(
                groups,
                state.regionsEditor,
                state.editPageItemNameEditor,
                state.datesIntervalEditor,
                state.tagCloudTargetingEditor,
                regionTree
            );

            const clientValidationErrors = new InternalGroupValidationSchema().validate(
                values(groupsData),
                selectGroupValidationParams(state)
            );

            const openedEditorsValidationResult = getGroupsOpenedEditorsValidationResult(
                values(groupsData),
                state
            );

            if (Object.keys(clientValidationErrors).length !== 0) {
                isValid = false;

                showErrorsActions.push(
                    Observable.of(
                        showGroupsClientValidationErrors(clientValidationErrors)
                    )
                );
            }

            if (openedEditorsValidationResult.valid === false) {
                isValid = false;

                showErrorsActions = [
                    ...showErrorsActions,
                    ...openedEditorsValidationResult.showErrorsActions
                ];
            }

            if (isValid === false) {
                return Observable.concat(
                    Observable.of(changeGroupEditPageStatus(PageStatus.LOADED)),
                    ...showErrorsActions
                );
            }

            const internalGroupsIds = Object.keys(groupsData);

            const allGroups: Array<EditGroupData> = internalGroupsIds.map(
                (groupId: GroupId) => groupsData[groupId]
            );

            let request$;
            let internalInput:
                | GdUpdateInternalAdGroupsInput
                | GdAddInternalAdGroupsInput;

            if (isNew) {
                internalInput = {
                    addItems: allGroups.map(mapNewGroupToInternalInput)
                };
                request$ = Observable.fromPromise(
                    deps.groupsMutationExecutor.addInternalGroups(
                        internalInput,
                        login
                    )
                );
            } else {
                internalInput = {
                    updateItems: allGroups.map(mapGroupToInternalInput)
                };
                request$ = Observable.fromPromise(
                    deps.groupsMutationExecutor.updateInternalGroups(
                        internalInput,
                        login
                    )
                );
            }

            return request$
                .mergeMap(
                    (
                        response:
                            | FetchResult<updateInternalAdGroups>
                            | FetchResult<addInternalAdGroups>
                    ) =>
                        processResponse(
                            response,
                            internalGroupsIds,
                            isNew,
                            true,
                            campaignId
                        )
                )
                .catch(
                    (
                        error:
                            | ApiMethodExecutingError
                            | NotSyncedError<
                                  | FetchResult<updateInternalAdGroups>
                                  | FetchResult<addInternalAdGroups>
                              >
                    ): Observable<Action> => {
                        if (error instanceof NotSyncedError) {
                            return processResponse(
                                error.data,
                                internalGroupsIds,
                                isNew,
                                false,
                                campaignId
                            );
                        }

                        return Observable.of(showGroupEditError(error));
                    }
                );
        }
    );
};

export const updateGroupNameEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(SAVE_GROUP_NAME_EDITOR_CHANGES),
        switchMap(
            (action: SaveGroupNameEditorChangesAction): Action[] => {
                const { groupId } = action;
                const state = store$.value;
                const { editPageItemNameEditor } = state;
                const editorId = GroupsHelpers.getGroupEditPageItemEditorId(
                    groupId
                );
                const editorData = selectEditPageItemNameEditorValue(
                    editPageItemNameEditor,
                    editorId
                );

                const validationErrors = getGroupNameEditorValidationResult(
                    state,
                    groupId
                );

                if (validationErrors.length === 0) {
                    return [
                        changeGroupName(groupId, editorData.value),
                        closeEditPageItemNameEditor(editorId)
                    ];
                }

                return [
                    showEditPageItemNameEditorErrors(editorId, validationErrors)
                ];
            }
        )
    );
};

export const openGroupRetargetingEditorEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store, deps) => {
    return action$
        .ofType(OPEN_GROUP_RETARGETING_EDITOR)
        .map((action: OpenGroupRetargetingEditor) => {
            const { editGroups } = store.value;
            const groupValue = editGroups.groups[action.groupId].value;

            if (groupValue.campaignType !== CampaignType.TEXT) {
                throw Error(
                    `В кампании с типом ${
                        groupValue.campaignType
                    } нет условий ретаргетинга`
                );
            }

            if (editGroups.retargetingConditions === undefined) {
                return dummyAction;
            }

            return openRetargetingEditor(
                action.groupId,
                editGroups.retargetingConditions.filter(
                    retargetingCondition =>
                        !retargetingCondition.isForRatesAdjustment
                ),
                groupValue.retargeting.map(
                    retargetingItem => retargetingItem.id
                )
            );
        });
};

export const saveGroupRetargetingEditorEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store, deps) => {
    return action$
        .ofType(SAVE_GROUP_RETARGETING_EDITOR)
        .mergeMap((action: SaveGroupRetargetingEditor) => {
            const {
                retargetingEditor,
                editGroups: { groups }
            } = store.value;
            const groupValue = groups[action.groupId].value;

            if (groupValue.campaignType !== CampaignType.TEXT) {
                throw Error(
                    `В кампании с типом ${
                        groupValue.campaignType
                    } нет условий ретаргетинга`
                );
            }

            return Observable.merge(
                Observable.of(
                    changeGroupRetargeting(
                        action.groupId,
                        mapRetargetingEditorValueToGroupRetargetingValue(
                            retargetingEditor[action.groupId].conditions,
                            groupValue.retargeting
                        )
                    )
                ),
                Observable.of(closeRetargetingEditor(action.groupId))
            );
        });
};

export const updateGroupRetargetingEditorEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store, deps) => {
    return action$
        .ofType(
            CHANGE_RETARGETING_LIST_ITEM_SELECTION,
            CHANGE_RETARGETING_LIST_SELECTION,
            DELETE_RETARGETING_LIST_ITEM
        )
        .map(
            (
                action:
                    | ChangeRetargetingListItemSelection
                    | ChangeRetargetingListSelection
                    | DeleteRetargetingListItem
            ) => resetRetargetingErrors(action.id)
        );
};

export const openPhrasesSelectorWizardForGroupsEditEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$
        .ofType(OPEN_PHRASES_SELECTOR_WIZARD_FOR_GROUPS_EDIT)
        .mergeMap(
            (
                action: OpenPhrasesSelectorWizardForGroupsEdit
            ): Observable<
                | OpenPhrasesSelectorWizard
                | UpdatePhrasesSelectorWizard
                | AddPhrasesStartInputItems
            > => {
                const state = store$.value;
                const group = state.editGroups.groups[action.groupId];
                const { keywordsEditor, id, campaign, value } = group;

                if (
                    value.campaignType !== CampaignType.TEXT &&
                    value.campaignType !== CampaignType.CPM_BANNER
                ) {
                    return Observable.empty();
                }

                const {
                    name,
                    minusKeywords = [],
                    regions: { regionIds }
                } = value;

                const startPhrases = keywordsEditor.value.map(item =>
                    mapPhraseToPhrasesSelectorListItem({
                        shows: 0,
                        phrase: item.phrase
                    })
                );

                return Observable.concat(
                    Observable.of(
                        openPhrasesSelectorWizard(
                            PhrasesSelectorWizardId.editGroupsPage,
                            {
                                id,
                                name,
                                campaign,
                                minusKeywords,
                                regionIds: regionIds,
                                startPhrases
                            }
                        )
                    ),
                    Observable.of(
                        addPhrasesStartInputItems(
                            PhrasesSelectorWizardId.editGroupsPage,
                            keywordsEditor.value.map(item => item.phrase)
                        )
                    )
                );
            }
        );
};

export const applySelectorWizardForGroupsEditEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.ofType(APPLY_SELECTOR_WIZARD_FOR_GROUP_EDIT).mergeMap(
        (action: ApplySelectorWizardForGroupEdit): Observable<Action> => {
            const state = store$.value;
            const editorData = state.minusPhraseLibraryEditor.data;
            const minusKeywordsEditor =
                editorData && editorData[action.groupId].minusKeywordsEditor;

            const resultActions: Action[] = [
                closePhrasesSelectorWizard(
                    PhrasesSelectorWizardId.editGroupsPage
                )
            ];

            if (minusKeywordsEditor && minusKeywordsEditor.isOpen) {
                const existingMinusWords = minusKeywordsEditor.value;

                resultActions.push(
                    changeMinusPhrasesEditor(
                        action.groupId,
                        existingMinusWords.concat(action.minusPhrases)
                    )
                );
            }

            return from(resultActions);
        }
    );
};

export const saveGroupRateCorrectionEditorEpic: DnaEpic = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(SAVE_GROUP_RATE_CORRECTIONS_EDITOR),
        mergeMap(
            (action: SaveGroupRateCorrectionsEditorAction): DnaEpicAction => {
                const state = store$.value;
                const groupEditorState =
                    state.rateCorrectionsEditor[action.id].value;

                const validationResult = validateRateCorrectionEditor(
                    groupEditorState
                );

                if (validationResult.isValid) {
                    return Observable.concat(
                        Observable.of(
                            updateRateCorrectionsEditorValidation(
                                action.id,
                                validationResult.errors
                            )
                        ),
                        Observable.of(
                            updateGroupRateCorrections(
                                action.id,
                                mapEditorRateCorrectionsToRateCorrections(
                                    groupEditorState
                                )
                            )
                        )
                    );
                } else {
                    return Observable.of(
                        updateRateCorrectionsEditorValidation(
                            action.id,
                            validationResult.errors
                        )
                    );
                }
            }
        )
    );
};

export const saveInterestsTargetingSetEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$
        .ofType(SAVE_INTERESTS_TARGETING_SET)
        .mergeMap((action: SaveInterestsTargetingSet) => {
            const state = store$.value;
            const interests = state.interestsTargetingEditor[action.id].value;

            return Observable.of(
                updateGroupAudienceTargetingInterests(action.id, interests)
            );
        });
};

export const updateInterestsTargetingSetEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$
        .ofType(UPDATE_GROUP_AUDIENCE_TARGETING_INTERESTS)
        .mergeMap((action: SaveInterestsTargetingSet) => {
            return Observable.of(closeInterestsTargetingEditor(action.id));
        });
};

export const saveCryptaSocialDemoEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(SAVE_CRYPTA_SOCIAL_DEMO),
        mergeMap((action: SaveCryptaSocialDemo) => {
            const state = store$.value;
            const socialDemoItems =
                state.cryptaSocialDemoEditor[action.id].checkedItems;

            return Observable.of(
                updateGroupAudienceCryptaSocialDemo(action.id, socialDemoItems)
            );
        })
    );
};

export const updateCryptaSocialDemoEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(UPDATE_GROUP_AUDIENCE_CRYPTA_SOCIAL_DEMO),
        mergeMap((action: UpdateGroupAudienceCryptaSocialDemo) => {
            return Observable.of(closeCryptaSocialDemoEditor(action.id));
        })
    );
};

export const saveCryptaGroupFamilyEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_CRYPTA_GROUP_FAMILY),
        mergeMap((action: SaveCryptaGroupFamily) => {
            const state = store$.value;
            const groupFamilyItems =
                state.cryptaGroupFamilyEditor[action.id].checkedItems;

            return of(
                updateGroupAudienceCryptaGroupFamily(
                    action.id,
                    groupFamilyItems
                )
            );
        })
    );
};

export const saveOutdoorMapActionEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(SAVE_OUTDOOR_MAP_ACTION),
        mergeMap((action: SaveOutdoorMapAction) => {
            const state = store$.value;
            const { checkedByRegion = {}, groupId } = state.outdoorMapEditor;

            return [
                updateGroupOutdoorMap(groupId, checkedByRegion),
                closeOutdoorMapEditor(),
                clearOutdoorErrors(groupId)
            ];
        })
    );
};

export const saveIndoorMapActionEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(SAVE_INDOOR_MAP_ACTION),
        mergeMap((action: SaveIndoorMapAction) => {
            const state = store$.value;
            const { checkedByRegion = {}, groupId } = state.indoorMapEditor;
            const screenGroupsByRegion = filterGroupIndoorMapData(
                state,
                groupId
            ).screenGroupsByRegion;

            return [
                updateGroupIndoorMap(
                    groupId,
                    checkedByRegion,
                    screenGroupsByRegion
                ),
                clearIndoorErrors(groupId),
                closeIndoorMapEditor()
            ];
        })
    );
};

export const updateCryptaGroupFamilyEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(UPDATE_GROUP_AUDIENCE_CRYPTA_GROUP_FAMILY),
        mergeMap((action: UpdateGroupAudienceCryptaGroupFamily) => {
            return of(closeCryptaGroupFamilyEditor(action.id));
        })
    );
};

export const saveCryptaAudioGenresEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_CRYPTA_AUDIO_GENRES),
        mergeMap((action: SaveCryptaAudioGenres) => {
            const state = store$.value;
            const audioGenresItems =
                state.cryptaAudioGenresEditor[action.id].checkedItems;

            return of(
                updateGroupAudienceCryptaAudioGenres(
                    action.id,
                    audioGenresItems
                )
            );
        })
    );
};

export const updateCryptaAudioGenresEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(UPDATE_GROUP_AUDIENCE_CRYPTA_AUDIO_GENRES),
        mergeMap((action: UpdateGroupAudienceCryptaAudioGenres) => {
            return of(closeCryptaAudioGenresEditor(action.id));
        })
    );
};

export const saveCryptaBehavioralEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(SAVE_CRYPTA_BEHAVIORAL),
        mergeMap((action: SaveCryptaBehavioral) => {
            const state = store$.value;
            const behavioralItems =
                state.cryptaBehavioralEditor[action.id].checkedItems;

            return Observable.of(
                updateGroupAudienceCryptaBehavioral(action.id, behavioralItems)
            );
        })
    );
};

export const updateCryptaBehavioralEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(UPDATE_GROUP_AUDIENCE_CRYPTA_BEHAVIORAL),
        mergeMap((action: UpdateGroupAudienceCryptaBehavioral) => {
            return Observable.of(closeCryptaBehavioralEditor(action.id));
        })
    );
};

export const leaveGroupsEditEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$
) => {
    return action$
        .ofType(LEAVE_GROUPS_EDIT)
        .mergeMap((action: LeaveGroupsEdit) => {
            const state = store$.value;
            const {
                commonData: {
                    regions,
                    client: { translocalGeotreeType },
                    login,
                    fromOldInterface
                },
                editGroups
            } = state;
            const accessRights = selectAccessRights(state);
            const operatorFeatures = selectOperatorFeatures(state);
            const editableTypes = selectEditableGroupTypes(
                operatorFeatures,
                accessRights
            );
            const regionTree: Region[] = regions
                ? RegionsHelpers.getRegionsTree(
                      regions,
                      LangUtils.getCurrentLang(),
                      translocalGeotreeType
                  )
                : [];

            const groupsData = getGroupsValueFromOpenedEditors(
                state.editGroups.groups,
                state.rateCorrectionsEditor,
                state.regionsEditor,
                state.retargetingEditor,
                state.minusPhraseLibraryEditor,
                state.editPageItemNameEditor,
                state.interestsTargetingEditor,
                state.cryptaSocialDemoEditor,
                state.cryptaGroupFamilyEditor,
                state.cryptaAudioGenresEditor,
                state.cryptaBehavioralEditor,
                regionTree,
                editableTypes
            );

            const wasDataChanged = Object.keys(groupsData).some(
                editGroupKey => {
                    return !isEqual(
                        groupsData[editGroupKey].value,
                        groupsData[editGroupKey].serverValue
                    );
                }
            );
            const { location } = action;

            let leavePage$: Array<Action>;

            if (fromOldInterface) {
                const cid = state.editGroups.campaignsIds[0];

                if (cid === undefined) {
                    throw new Error('не задан номер кампании');
                }

                leavePage$ = [
                    changeLocationHard(
                        UrlHelpers.getShowCampaignUrl(cid, login)
                    ),
                    resetGroupsEdit(),
                    resetRetargetingEditors(),
                    resetRateCorrectionsEditors()
                ];
            } else {
                const isCampaignCpmBanner = some(
                    editGroups.groups,
                    group =>
                        group.value.campaignType === CampaignType.CPM_BANNER
                );
                const typeLocation =
                    typeof location !== 'string'
                        ? location.query
                        : { ulogin: login };
                const calcType = isCampaignCpmBanner
                    ? CampaignsCalcTypeFilter.CPM
                    : CampaignsCalcTypeFilter.CPC;

                leavePage$ = [
                    changeLocation(
                        typeof location !== 'string'
                            ? location.pathname
                            : location,
                        {
                            ...typeLocation,
                            [GridScreenUrlSearchQueryParams.calcType]: calcType
                        }
                    ),
                    resetGroupsEdit(),
                    resetRetargetingEditors(),
                    resetRateCorrectionsEditors()
                ];
            }
            const isInternal = Object.keys(groupsData).some(key => {
                if (groupsData[key].value.campaignType) {
                    return [
                        CampaignType.INTERNAL_DISTRIB,
                        CampaignType.INTERNAL_FREE
                    ].includes(groupsData[key].value
                        .campaignType as CampaignType);
                }

                return false;
            });

            if (wasDataChanged && action.showConfirm) {
                return Observable.fromPromise(
                    confirm({
                        message: isInternal
                            ? i18n('warning-about-changes-internal')
                            : i18n('warning-about-changes')
                    })
                )
                    .mergeMap(() => {
                        return leavePage$;
                    })
                    .catch(() => EMPTY);
            }

            return leavePage$;
        });
};

export const resetGroupsEditEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(RESET_GROUPS_EDIT),
        map(
            (action: ResetGroupsEdit): ResetRegionsEditor => {
                return resetRegionsEditor();
            }
        )
    );
};

export const cancelGroupEditorRegionsEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.ofType(CANCEL_GROUP_EDITOR_REGIONS).mergeMap(
        (action: CancelGroupEditorRegions): Action[] => {
            return [
                cancelChangesRegionsEditor(action.groupId, action.selectedMap),
                clearErrorsGroupEditorRegions(action.groupId)
            ];
        }
    );
};

export const saveGroupEditorRegionEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.ofType(SAVE_GROUP_EDITOR_REGIONS).map(
        (action: SaveGroupEditorRegions): Action => {
            const hasSelectedRegions = some(
                action.selectedMap,
                item => item === true
            );

            if (!hasSelectedRegions) {
                return showGroupEditorRegionsErrors(action.groupId, [
                    {
                        text: i18n('geo-empty-regions')
                    }
                ]);
            }

            return updateGroupEditorRegions(
                action.groupId,
                action.selectedMap,
                action.tree
            );
        }
    );
};

export const updateGroupEditorRegionEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.ofType(UPDATE_GROUP_EDITOR_REGIONS).map(
        (action: SaveGroupEditorRegions): Action => {
            return closeRegionsEditor(action.groupId);
        }
    );
};

export const deleteRetargetingListItemFromGroupEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.ofType(DELETE_RETARGETING_LIST_ITEM).map(
        (
            action: DeleteRetargetingListItem
        ): DeleteRetargetingConditionFromGroup => {
            return deleteRetargetingConditionFromGroup(action.id, action.index);
        }
    );
};

export const updateRetargetingListItemEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$
        .ofType(UPDATE_RETARGETING_LIST_ITEM, ADD_RETARGETING_LIST_ITEM)
        .map((action: UpdateRetargetingListItem) => {
            return updateRetargetingConditions(action.conditionData);
        });
};

export const openGroupFilterInspectorEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(OPEN_GROUP_FILTER_INSPECTOR),
        mergeMap((action: OpenGroupFilterInspectorAction) => {
            const {
                editGroups: {
                    groupFilterInspector: { feedId }
                },
                commonData: { login }
            } = store$.value;

            if (!feedId) {
                throw new Error('the group has an invalid feedId');
            }

            return from(deps.groupsDataProvider.getFeeds(login)).pipe(
                map(result => result.data.client.feeds.rowset),
                mergeMap(
                    (originFeeds: GroupsQueryForEdit_client_feeds_rowset[]) => {
                        const { feeds } = mapFeeds(originFeeds);

                        const { categories, feedType, businessType } = feeds[
                            feedId
                        ];

                        return from(
                            deps.groupsDataProvider.getSmartFeedSchema(
                                businessType,
                                feedType
                            )
                        ).pipe(
                            mergeMap(({ data }) => {
                                return from([
                                    setFilterFeedCategories(categories),
                                    updateFeedFilterSchema(
                                        mapFeedFilterSchemaFromApiResponse(data)
                                    )
                                ]);
                            }),
                            catchError(() => of(dummyAction))
                        );
                    }
                )
            );
        })
    );
};

const DELAY_TIME_TO_START = 0;
const TIME_TO_REPEAT_REQUEST = 3000;

export const pollingFeedsData: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(PERFORMANCE_SET_VISIBLE_GROUP_BANNER_POPUP_FEED),
        filter(
            (action: SetVisibleGroupBannerPopupFeedAction) => action.isVisible
        ),
        switchMap(() => {
            const login = store$.value.commonData.login;

            return timer(DELAY_TIME_TO_START, TIME_TO_REPEAT_REQUEST).pipe(
                takeUntil(
                    action$.pipe(
                        ofType(PERFORMANCE_SET_VISIBLE_GROUP_BANNER_POPUP_FEED),
                        filter(
                            (action: SetVisibleGroupBannerPopupFeedAction) => {
                                const {
                                    groupsIds,
                                    groups
                                } = store$.value.editGroups;
                                let noGroupHasOpenPopup = true;

                                for (let i = 0; i < groupsIds.length; i++) {
                                    const groupId = groupsIds[i];
                                    const group = groups[groupId];

                                    // если у группы есть открытый попап с выбором фида, то нужно отфильтровать данный экшен
                                    if (
                                        group.value.campaignType ===
                                            CampaignType.PERFORMANCE &&
                                        group.value.feedsPopupIsOpen
                                    ) {
                                        noGroupHasOpenPopup = false;
                                        break;
                                    }
                                }

                                // эпик будет слушать "takeUntil" до тех пор, пока не придет экшен на закрытие попапа и не будет других открытых попапов
                                return !action.isVisible && noGroupHasOpenPopup;
                            }
                        )
                    )
                ),
                exhaustMap(() =>
                    from(deps.groupsDataProvider.getFeeds(login)).pipe(
                        map(result => result.data.client.feeds.rowset),
                        map(
                            (
                                originFeeds: GroupsQueryForEdit_client_feeds_rowset[]
                            ) => {
                                const { feedsIds, feeds } = mapFeeds(
                                    originFeeds
                                );
                                return updateFeeds(feedsIds, feeds);
                            }
                        )
                    )
                ),
                catchError(() => of(dummyAction))
            );
        })
    );
};

export const createTextGroupEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(CREATE_TEXT_AD_GROUP),
        mergeMap(
            ({
                campaignId,
                name,
                replaceLocation
            }: CreateTextAdGroupAction) => {
                const {
                    commonData: { login }
                } = store$.value;
                return from(
                    createTextAdGroup(deps.groupsMutationExecutor, {
                        login,
                        campaignId,
                        name
                    })
                ).pipe(
                    map(({ adGroupId }) =>
                        goToGroupsEdit(
                            login,
                            [campaignId],
                            [adGroupId],
                            true,
                            replaceLocation
                        )
                    ),
                    catchError((err: Error | ApiMethodExecutingError) => {
                        const message =
                            err instanceof ApiMethodExecutingError
                                ? err.getMessage()
                                : err.message || i18n('create-group-error');

                        showNoticeMessage(NoticeType.error, message);
                        return Observable.of(dummyAction);
                    })
                );
            }
        )
    );
};

export const createSmartGroupEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(CREATE_SMART_AD_GROUP),
        mergeMap(
            ({
                campaignId,
                feedId,
                name,
                mockGroupId
            }: CreateSmartAdGroupAction) => {
                const {
                    commonData: {
                        login,
                        regions,
                        client: { translocalGeotreeType }
                    }
                } = store$.value;
                const smartInput = {
                    campaignId,
                    feedId,
                    name
                };

                return from(
                    deps.groupsMutationExecutor.createSmartGroup(
                        smartInput,
                        login
                    )
                ).pipe(
                    mergeMap(
                        (result): Observable<Action> => {
                            const { data } = result;
                            if (!data) {
                                throw new Error();
                            }

                            const { validationResult } = data.addSmartAdGroups;
                            if (
                                validationResult &&
                                validationResult.errors.length
                            ) {
                                const errorMapper = createSmartGroupAddHandleErrorMapper(
                                    mockGroupId
                                );
                                const validationErrors = errorMapper.mapServerErrorsToClientError(
                                    validationResult.errors
                                );

                                return of(
                                    showGroupsPartialSaveResult({
                                        errors: validationErrors,
                                        updatedGroupIds: []
                                    })
                                );
                            }

                            const [
                                createdGroupData
                            ] = data.addSmartAdGroups.addedAdGroupItems;

                            if (
                                !createdGroupData ||
                                !createdGroupData.adGroupId
                            ) {
                                throw new Error();
                            }

                            const { adGroupId } = createdGroupData;

                            const regionsTree =
                                RegionsHelpers.getRegionsTree(
                                    regions || {},
                                    LangUtils.getCurrentLang(),
                                    translocalGeotreeType
                                ) || [];

                            GroupIdsForGroupsEditHelpers.setCampAndGroupIds(
                                login,
                                [adGroupId],
                                [campaignId]
                            );

                            const group =
                                store$.value.editGroups.groups[mockGroupId]
                                    .value;
                            let regionIds = [0];
                            switch (group.campaignType) {
                                case CampaignType.PERFORMANCE:
                                    regionIds = group.regions.regionIds;
                                    break;
                                default:
                                    break;
                            }

                            return merge(
                                of(
                                    changeLocation(
                                        UrlHelpers.getGroupsEditUrl({
                                            [GroupsScreenUrlSearchQueryParams.campaignsIds]: [
                                                campaignId
                                            ],
                                            [GroupsScreenUrlSearchQueryParams.groupsIds]: [
                                                adGroupId
                                            ],
                                            [GroupsScreenUrlSearchQueryParams.isNew]: undefined,
                                            [GroupsScreenUrlSearchQueryParams.isNewEmptyGroup]: store$
                                                .value.editGroups
                                                .isNewEmptyGroup
                                                ? '1'
                                                : undefined,
                                            [GroupsScreenUrlSearchQueryParams.ulogin]: login
                                        }),
                                        undefined,
                                        false
                                    )
                                ),
                                of(
                                    moveNewGroupToCreated(
                                        mockGroupId,
                                        adGroupId,
                                        feedId
                                    )
                                ),
                                !regionIds.length
                                    ? from(
                                          deps.groupsDataProvider.getGroups(
                                              login,
                                              [adGroupId],
                                              [campaignId]
                                          )
                                      ).pipe(
                                          mergeMap(({ data: queryData }) => {
                                              const rawGroup = queryData.client.adGroups.rowset.find(
                                                  ({ id }) => id === adGroupId
                                              );
                                              if (!rawGroup) {
                                                  throw new Error();
                                              }
                                              return from([
                                                  saveGroupEditorRegions(
                                                      adGroupId,
                                                      RegionsHelpers.getSelectedMapFromRawRegionData(
                                                          regionsTree,
                                                          rawGroup.regionsInfo
                                                              .regionIds
                                                      ),
                                                      regionsTree
                                                  ),
                                                  saveGroups(false)
                                              ]);
                                          })
                                      )
                                    : of(saveGroups(false))
                            );
                        }
                    ),
                    catchError(
                        (
                            error: Error | ApiMethodExecutingError
                        ): Observable<ShowGroupEditErrorAction> => {
                            const message =
                                error instanceof ApiMethodExecutingError
                                    ? error.getMessage()
                                    : error.message ||
                                      i18n('create-group-error');

                            return Observable.of(showGroupEditError(message));
                        }
                    )
                );
            }
        )
    );
};

export const saveGroupMinusPhrasesEditorEpic: DnaEpic = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(SAVE_GROUP_MINUS_PHRASES_EDITOR),
        mergeMap(
            (action: SaveGroupMinusPhrasesEditorAction): Observable<Action> => {
                const state = store$.value;

                const {
                    libraryItems,
                    data: {
                        [action.groupId]: { minusKeywordsEditor }
                    }
                } = state.minusPhraseLibraryEditor;

                if (state.commonData.features.isMinusWordsLibEnabled) {
                    const offset = 0;
                    const constants = state.commonData.constants;

                    if (!constants) {
                        return EMPTY;
                    }

                    const limit =
                        constants.minusPhraseLibraryConstants
                            .maxLibraryPacksCount;

                    // Заново запросить минус-фразы на случай, если были выбраны удаленные элементы
                    return from(
                        deps.minusPhraseLibraryDataProvider.fetchMinusPhraseLibraryData(
                            offset,
                            limit
                        )
                    ).pipe(
                        mergeMap(
                            (
                                response: FetchMinusPhraseLibraryDataResult
                            ): Action[] => {
                                const updatedLibraryItems = mapFetchResultToLibraryItem(
                                    response.data
                                );

                                return [
                                    updateGroupMinusPhrases(
                                        action.groupId,
                                        minusKeywordsEditor,
                                        updatedLibraryItems
                                    ),
                                    closeMinusPhrasesEditor(action.groupId)
                                ];
                            }
                        )
                    );
                }

                return from([
                    updateGroupMinusPhrases(
                        action.groupId,
                        minusKeywordsEditor,
                        libraryItems || []
                    ),
                    closeMinusPhrasesEditor(action.groupId)
                ]);
            }
        )
    );
};

export const handleGroupsEditMassActionsEpic: DnaEpic = action$ => {
    return action$.pipe(
        ofType(HANDLE_GROUPS_EDIT_MASS_ACTIONS),
        mergeMap(
            ({ action }: HandleGroupsEditMassActions): Observable<Action> => {
                switch (action) {
                    case GroupsEditMassActionItemType.minusPhrase:
                        return of(
                            openMinusPhraseGroupsMassActions(
                                MassActionId.groupsEdit,
                                []
                            )
                        );
                    default:
                        return EMPTY;
                }
            }
        )
    );
};
